import type { ReactNode } from 'react';
import React from 'react';

import { Navigate } from 'react-router';

import { pipe } from '@code-expert/prelude';
import { concatRoutes, parser } from '/imports/modules/Routing';
import { Authorise } from '/imports/ui/components/Authorise';
import { TopNavLayout } from '/imports/ui/layouts/TopNavLayout';
import type { Page } from './routes';
import {
  backupRoute,
  messagesLink,
  messagesRoute,
  notificationsRoute,
  profileRoute,
  securityRoute,
  userLink,
  userRoute,
  zoomRoute,
} from './routes';

const UserBackup = React.lazy(() => import('/imports/modules/Backup/presentation/UserBackup'));

const MessagesPage = React.lazy(() => import('/imports/ui/pages/user/messages/MessagesPage'));
const Notifications = React.lazy(
  () => import('/imports/ui/pages/user/notifications/Notifications'),
);
const Security = React.lazy(() => import('/imports/ui/pages/user/security/Security'));
const Zoom = React.lazy(() => import('/imports/ui/pages/user/zoom/Zoom'));

const Profile = React.lazy(() => import('/imports/ui/pages/user/profile/Profile'));
const Navigation = React.lazy(() => import('./UserNavigation'));

const UserPage = ({ page, children }: { page: Page; children: ReactNode }) => (
  <Authorise
    deny={['lti']}
    layout={(children) => <TopNavLayout selected="cxUserMenu">{children}</TopNavLayout>}
  >
    <Navigation page={page}>{children}</Navigation>
  </Authorise>
);

export const userRouter = concatRoutes([
  pipe(
    profileRoute.parser,
    parser.map(() => (
      <UserPage page="profile">
        <Profile />
      </UserPage>
    )),
  ),
  pipe(
    messagesRoute.parser,
    parser.map(({ section }) => (
      <UserPage page="messages">
        <MessagesPage section={section} sectionLink={(section) => messagesLink({ section })} />
      </UserPage>
    )),
  ),
  pipe(
    notificationsRoute.parser,
    parser.map(() => (
      <UserPage page="notifications">
        <Notifications />
      </UserPage>
    )),
  ),
  pipe(
    securityRoute.parser,
    parser.map(({ section }) => (
      <UserPage page="security">
        <Security section={section} />
      </UserPage>
    )),
  ),
  pipe(
    notificationsRoute.parser,
    parser.map(() => (
      <UserPage page="notifications">
        <Notifications />
      </UserPage>
    )),
  ),
  pipe(
    zoomRoute.parser,
    parser.map(() => (
      <UserPage page="zoom">
        <Zoom />
      </UserPage>
    )),
  ),
  pipe(
    backupRoute.parser,
    parser.map(() => (
      <UserPage page="backup">
        <UserBackup />
      </UserPage>
    )),
  ),
  pipe(
    userRoute.parser,
    parser.map(() => <Navigate to={userLink({})} />),
  ),
]);
